
// Deployment trigger: 2026-04-24
import React, { useState, useEffect } from 'react';
import { Ticket, Clock, Trophy, Star, Sparkles, ChevronRight } from 'lucide-react';
import { Prize } from '../../types';
import { CountdownUnit } from './Common';

interface RaffleProps {
  prizes: Prize[];
  endDate?: string;
  onStart: () => void;
  isLightMode?: boolean;
}

const Raffle: React.FC<RaffleProps> = ({ prizes, endDate, onStart, isLightMode }) => {
  const [timeLeft, setTimeLeft] = useState<{days:number, hours:number, mins:number, secs:number} | null>(null);

  useEffect(() => {
    if (endDate) {
      const timer = setInterval(() => {
        const [year, month, day] = endDate.split('-').map(Number);
        const target = new Date(year, month - 1, day, 23, 59, 59).getTime();
        const now = new Date().getTime();
        const diff = target - now;
        
        if (diff <= 0) {
          setTimeLeft({ days: 0, hours: 0, mins: 0, secs: 0 });
          clearInterval(timer);
        } else {
          setTimeLeft({
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            mins: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            secs: Math.floor((diff % (1000 * 60)) / 1000),
          });
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [endDate]);

  const getTierLabel = (index: number) => {
    if (index === 0) return 'Grand Prize';
    if (index === 1) return '2nd Prize';
    if (index === 2) return '3rd Prize';
    return `${index + 1}th Prize`;
  };

  const getTierColor = (index: number) => {
    if (index === 0) return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
    if (index === 1) return 'text-slate-500 bg-slate-500/10 border-slate-500/20';
    if (index === 2) return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
    return 'text-indigo-600 bg-indigo-600/10 border-indigo-600/20';
  };

  return (
    <div className="flex flex-col items-center w-full relative">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 animate-pulse pointer-events-none">
        <Sparkles className="w-6 h-6 text-indigo-400 opacity-40" />
      </div>
      <div className="absolute bottom-20 right-10 animate-bounce duration-[3s] pointer-events-none">
        <Star className="w-4 h-4 text-amber-400 opacity-40" />
      </div>

      <div className="relative z-10 text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 font-black text-[10px] uppercase tracking-widest mb-6 animate-pulse">
          <Clock className="w-3.5 h-3.5" /> Lucky Draw Countdown
        </div>
        {timeLeft && (
          <div className="flex gap-4 justify-center items-center">
            <CountdownUnit value={timeLeft.days} label="Days" isLightMode={true} />
            <CountdownUnit value={timeLeft.hours} label="Hrs" isLightMode={true} />
            <CountdownUnit value={timeLeft.mins} label="Min" isLightMode={true} />
            <CountdownUnit value={timeLeft.secs} label="Sec" isLightMode={true} />
          </div>
        )}
      </div>

      <div 
        className="relative z-10 w-full space-y-4 mb-10 overflow-y-auto max-h-[250px] pr-2 scrollbar-hide overscroll-contain touch-pan-y"
        onTouchMove={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <h4 className="text-slate-400 font-black text-[10px] uppercase tracking-[0.3em] text-center mb-4">The Prize Pool</h4>
        
        {prizes.map((prize, idx) => (
          <div 
            key={prize.id} 
            className={`relative group bg-gradient-to-br from-slate-50 to-white rounded-[32px] p-5 border transition-all hover:scale-[1.02] ${idx === 0 ? 'border-amber-200 shadow-sm' : 'border-slate-100'}`}
          >
            <div className="flex items-center gap-5">
              {prize.imageUrl ? (
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border overflow-hidden ${getTierColor(idx)}`}>
                  <img src={prize.imageUrl} alt={prize.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                </div>
              ) : (
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${getTierColor(idx)}`}>
                  {idx === 0 ? <Trophy className="w-6 h-6" /> : <Star className="w-5 h-5" />}
                </div>
              )}
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[9px] font-black uppercase tracking-widest block ${idx === 0 ? 'text-amber-500' : 'text-indigo-500 opacity-80'}`}>
                    {getTierLabel(idx)}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {prize.winnersCount || 1} {prize.winnersCount === 1 || !prize.winnersCount ? 'Winner' : 'Winners'}
                  </span>
                </div>
                <h5 className="text-slate-800 font-black text-lg truncate tracking-tight">
                  {prize.name}
                </h5>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative z-10 w-full">
        <button 
          onClick={onStart} 
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 rounded-3xl font-black text-lg tracking-[0.1em] shadow-2xl shadow-indigo-500/20 transition-all flex items-center justify-center gap-4 group uppercase relative overflow-hidden active:scale-95"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
          <Ticket className="w-6 h-6 group-hover:rotate-12 transition-transform" /> 
          Join the Raffle
          <ChevronRight className="w-5 h-5 opacity-50 group-hover:translate-x-1 transition-transform" />
        </button>
        <p className="text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest mt-6 opacity-80">
          Enter your details on the next step to get your ticket
        </p>
      </div>
    </div>
  );
};

export default Raffle;
